/**
 * When a storm is worth a text, and what the text should say it was.
 *
 * This is deliberately a *different* question from the one
 * `stormReport.ts` answers. That module builds evidence for an adjuster: every
 * row citable, nothing rounded in our favour, a hostile reader assumed. This
 * one decides whether to interrupt forty people's afternoon, and then writes
 * one sentence about why.
 *
 * The two failure modes are not symmetric, which is what sets the thresholds:
 *
 *   * **Texting about a storm that was not one** costs the relationship. A
 *     partner who gets "big storm yesterday!" about a shower they slept through
 *     stops reading the next one, and there is no way to earn that back.
 *   * **Missing a real one** costs a week of claims. Recoverable — Jonathon can
 *     draft a batch by hand for any date, which is why that button exists.
 *
 * So the triggers are meant to be specific rather than sensitive, except for
 * hail, where any report at all counts. Hail is the peril that pays for a roof,
 * a quarter-inch stone on a deck is a conversation, and by the time hail is
 * large enough to clear a threshold everybody in the county already knows.
 *
 * Every number here is a default. The live values come from `storm_config`, so
 * a season of near-misses can be tuned from a screen rather than a deploy.
 */

import { parseLsrCsv, type LsrRow } from "./stormReport.js";

/* ---------------------------------------------------------- the counties -- */

/**
 * The two counties, and how each is named by each source.
 *
 * The UGC code is how the National Weather Service identifies a county in an
 * alert; the ASOS station is the airport whose rain gauge and anemometer stand
 * in for the county. Birmingham-Shuttlesworth (KBHM) sits in Jefferson;
 * Shelby County Airport (KEET) is at Alabaster.
 *
 * `lsrName` is what the Local Storm Report feed calls the county, upper case
 * and without the word "County" — matching how IEM writes it.
 */
export const WATCHED_COUNTIES = [
  {
    key: "shelby" as const,
    label: "Shelby County",
    ugc: "ALC117",
    asos: "KEET",
    lsrName: "SHELBY",
  },
  {
    key: "jefferson" as const,
    label: "Jefferson County",
    ugc: "ALC073",
    asos: "KBHM",
    lsrName: "JEFFERSON",
  },
] as const;

export type WatchedCounty = (typeof WATCHED_COUNTIES)[number]["key"];

export function countyForUgc(ugc: string): WatchedCounty | null {
  return (
    WATCHED_COUNTIES.find((county) => county.ugc === ugc.trim().toUpperCase())
      ?.key ?? null
  );
}

export function countyForLsrName(name: string): WatchedCounty | null {
  const cleaned = name
    .trim()
    .toUpperCase()
    .replace(/\s+COUNTY$/, "");
  return (
    WATCHED_COUNTIES.find((county) => county.lsrName === cleaned)?.key ?? null
  );
}

/* -------------------------------------------------------------- triggers -- */

export const STORM_TRIGGERS = [
  "hail",
  "wind",
  "heavy_rain",
  "flood_warning",
] as const;

export type StormTrigger = (typeof STORM_TRIGGERS)[number];

/**
 * The thresholds, as shipped.
 *
 * Mirrored into `storm_config` by the migration that creates it. These are the
 * fallback for anything that reads a key the table does not hold — a new
 * threshold added in code before somebody has run the migration would
 * otherwise evaluate as zero, which for `wind_mph` means every gust in Alabama
 * fires a batch.
 */
export const DEFAULT_STORM_THRESHOLDS = {
  /** Inches of rain in 24 hours at the county's ASOS station. */
  rain_in_24h: 1.0,
  /** Miles per hour, from a Local Storm Report gust or damage report. */
  wind_mph: 60,
  /** Knots, measured at KBHM or KEET. 52 kt is 60 mph. */
  station_gust_kt: 52,
  /** Inches. Zero: any hail report at all. */
  hail_in: 0,
  /** Days to leave between two texts to the same partner. */
  rate_guard_days: 10,
  /** Days between monthly texts. */
  monthly_days: 30,
} as const;

/**
 * The live thresholds. Numbers, not the literals the defaults happen to be —
 * every one of these is edited from a screen, and a type that pinned
 * `wind_mph` to 60 would refuse the tuned value it exists to carry.
 */
export type StormThresholds = {
  [K in keyof typeof DEFAULT_STORM_THRESHOLDS]: number;
};

/** Fill any missing key from the shipped defaults. */
export function thresholdsFrom(
  rows: readonly { key: string; value: number | string }[],
): StormThresholds {
  const merged: Record<string, number> = { ...DEFAULT_STORM_THRESHOLDS };
  for (const row of rows) {
    const value = typeof row.value === "number" ? row.value : Number(row.value);
    if (Number.isFinite(value) && row.key in merged) merged[row.key] = value;
  }
  return merged as StormThresholds;
}

/* ------------------------------------------------------------ the sources -- */

/** The alert products worth looking at. */
export const WATCHED_ALERT_EVENTS = [
  "Flood Warning",
  "Flash Flood Warning",
  "Severe Thunderstorm Warning",
  "Tornado Warning",
] as const;

/**
 * The two alerts that fire a batch on their own.
 *
 * A Flood or Flash Flood Warning means water is already somewhere it should not
 * be, which is a roof conversation. A Severe Thunderstorm or Tornado Warning is
 * a *forecast* — issued for a storm that may pass ten miles away and do
 * nothing — so those are recorded and shown on the storms page but never
 * trigger by themselves. If that storm did anything, an LSR will say so.
 */
export const ALERTS_THAT_TRIGGER = ["Flood Warning", "Flash Flood Warning"];

export interface NwsAlert {
  event: string;
  ugc: string[];
  effective: string;
  ends: string | null;
  headline: string | null;
  id: string;
}

/**
 * Pull the fields worth keeping out of `api.weather.gov/alerts`.
 *
 * The API returns GeoJSON with a good deal more in it than this, including full
 * polygon geometry. Only these fields are stored: `raw` on a storm event is
 * read by the public report page and by a person debugging a false positive,
 * and a megabyte of coordinate pairs helps neither.
 */
export function parseNwsAlerts(payload: unknown): NwsAlert[] {
  const features = (payload as { features?: unknown })?.features;
  if (!Array.isArray(features)) return [];

  const alerts: NwsAlert[] = [];
  for (const feature of features) {
    const properties = (feature as { properties?: Record<string, unknown> })
      ?.properties;
    if (!properties) continue;

    const event = String(properties.event ?? "").trim();
    if (!event) continue;

    const geocode = properties.geocode as { UGC?: unknown } | undefined;
    const ugc = Array.isArray(geocode?.UGC) ? geocode.UGC.map(String) : [];

    alerts.push({
      id: String(properties.id ?? ""),
      event,
      ugc,
      effective: String(properties.effective ?? properties.sent ?? ""),
      ends: properties.ends ? String(properties.ends) : null,
      headline: properties.headline ? String(properties.headline) : null,
    });
  }
  return alerts;
}

/** Alerts covering one of the two counties, of a type worth recording. */
export function alertsForCounty(
  alerts: readonly NwsAlert[],
  county: WatchedCounty,
): NwsAlert[] {
  const ugc = WATCHED_COUNTIES.find((row) => row.key === county)!.ugc;
  const watched = new Set<string>(WATCHED_ALERT_EVENTS);
  return alerts.filter(
    (alert) => watched.has(alert.event) && alert.ugc.includes(ugc),
  );
}

/* ------------------------------------------------- local storm reports -- */

export interface CountyLsr {
  county: WatchedCounty;
  /** UTC timestamp of record, as the feed wrote it. */
  valid: string;
  type: string;
  /** Inches for hail, mph for wind, null when the report carried no number. */
  magnitude: number | null;
  city: string;
  source: string;
  remark: string;
  lat: number | null;
  lon: number | null;
}

const numberOrNull = (value: string | undefined): number | null => {
  const raw = (value ?? "").trim();
  if (!raw || raw.toUpperCase() === "NONE") return null;
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Local Storm Reports in the two counties, from the IEM CSV feed.
 *
 * Reuses `parseLsrCsv` rather than parsing again: it already handles the byte
 * order mark, quoted remarks containing commas, and CRLF, all of which the
 * feed produces and all of which were found the hard way when the storm history
 * report was built.
 *
 * A report outside both counties is dropped here rather than filtered later,
 * so that `raw` on a storm event holds what was relevant and not the whole
 * state's afternoon.
 */
export function parseCountyLsrs(csv: string): CountyLsr[] {
  const rows: LsrRow[] = parseLsrCsv(csv);
  const found: CountyLsr[] = [];

  for (const row of rows) {
    // The UGC code first: it is the Weather Service's own identifier and is
    // unambiguous, where COUNTY is a display name that arrives title-cased,
    // upper-cased or with "County" on the end depending on the feed. The name
    // is the fallback for an older row that carries no UGC.
    const county =
      countyForUgc(row.UGC ?? "") ?? countyForLsrName(row.COUNTY ?? "");
    if (!county) continue;

    found.push({
      county,
      valid: (row.VALID ?? row.VALID2 ?? "").trim(),
      type: (row.TYPETEXT ?? "").trim().toUpperCase(),
      magnitude: numberOrNull(row.MAG),
      city: (row.CITY ?? "").trim(),
      source: (row.SOURCE ?? "").trim(),
      remark: (row.REMARK ?? "").trim(),
      lat: numberOrNull(row.LAT),
      lon: numberOrNull(row.LON),
    });
  }
  return found;
}

const HAIL_TYPES = new Set(["HAIL"]);
const WIND_TYPES = new Set([
  "TSTM WND GST",
  "TSTM WND DMG",
  "NON-TSTM WND GST",
  "NON-TSTM WND DMG",
  "HIGH WIND",
]);
const RAIN_TYPES = new Set(["HEAVY RAIN", "FLASH FLOOD", "FLOOD"]);

/* ----------------------------------------------------- station readings -- */

export interface StationReading {
  station: string;
  /** Inches of rain over the 24 hours ending at `observedAt`. */
  precipIn: number | null;
  /** Peak gust in knots. */
  peakGustKt: number | null;
  observedAt: string;
}

/* --------------------------------------------------------- the judgement -- */

export interface CountyEvidence {
  county: WatchedCounty;
  /** `YYYY-MM-DD`, America/Chicago. */
  eventDate: string;
  alerts: NwsAlert[];
  lsrs: CountyLsr[];
  station: StationReading | null;
}

export interface StormAssessment {
  county: WatchedCounty;
  eventDate: string;
  triggers: StormTrigger[];
  maxHailIn: number | null;
  maxWindMph: number | null;
  rainIn24h: number | null;
  /** Towns named in the hail reports, for the summary sentence. */
  hailTowns: string[];
  /** True when nothing cleared a threshold and no batch should be drafted. */
  quiet: boolean;
}

/**
 * Did anything happen here worth telling a partner about?
 *
 * Each trigger is independent and any one of them is enough. They are collected
 * rather than short-circuited because the summary sentence names all of them —
 * "1.4 inches of rain and 62 mph gusts" is a different text from "1.4 inches of
 * rain", and the difference is what makes it read as somebody who looked.
 */
export function assessCounty(
  evidence: CountyEvidence,
  thresholds: StormThresholds = DEFAULT_STORM_THRESHOLDS,
): StormAssessment {
  const triggers = new Set<StormTrigger>();

  for (const alert of evidence.alerts) {
    if (ALERTS_THAT_TRIGGER.includes(alert.event)) {
      triggers.add("flood_warning");
      triggers.add("heavy_rain");
    }
  }

  let maxHailIn: number | null = null;
  let maxWindMph: number | null = null;
  const hailTowns: string[] = [];

  for (const report of evidence.lsrs) {
    if (HAIL_TYPES.has(report.type)) {
      const size = report.magnitude;
      // A hail report with no size on it still counts. The threshold is zero by
      // default, and a stone somebody did not measure is still a stone.
      if (size === null || size >= thresholds.hail_in) {
        triggers.add("hail");
        if (report.city && !hailTowns.includes(report.city)) {
          hailTowns.push(report.city);
        }
      }
      if (size !== null && (maxHailIn === null || size > maxHailIn)) {
        maxHailIn = size;
      }
      continue;
    }

    if (WIND_TYPES.has(report.type)) {
      const speed = report.magnitude;
      if (speed !== null) {
        if (maxWindMph === null || speed > maxWindMph) maxWindMph = speed;
        if (speed >= thresholds.wind_mph) triggers.add("wind");
      } else if (report.type.endsWith("DMG")) {
        // Damage with no speed. The Weather Service logged it because something
        // broke, which is exactly the conversation a partner's client is about
        // to have — so it triggers, even though there is no number to print.
        triggers.add("wind");
      }
      continue;
    }

    if (RAIN_TYPES.has(report.type)) {
      triggers.add("heavy_rain");
    }
  }

  let rainIn24h: number | null = null;
  if (evidence.station) {
    rainIn24h = evidence.station.precipIn;
    if (rainIn24h !== null && rainIn24h >= thresholds.rain_in_24h) {
      triggers.add("heavy_rain");
    }
    const gust = evidence.station.peakGustKt;
    if (gust !== null) {
      const mph = gust * 1.15078;
      if (maxWindMph === null || mph > maxWindMph) maxWindMph = Math.round(mph);
      if (gust >= thresholds.station_gust_kt) triggers.add("wind");
    }
  }

  // The order the storms page and the summary read them in — worst first.
  const order: StormTrigger[] = ["hail", "wind", "heavy_rain", "flood_warning"];

  return {
    county: evidence.county,
    eventDate: evidence.eventDate,
    triggers: order.filter((trigger) => triggers.has(trigger)),
    maxHailIn,
    maxWindMph,
    rainIn24h,
    hailTowns,
    quiet: triggers.size === 0,
  };
}

/* ------------------------------------------------------------ the wording -- */

const HAIL_SIZES: [number, string][] = [
  [4.0, "softball-size"],
  [2.75, "baseball-size"],
  [2.0, "hen egg-size"],
  [1.75, "golf ball-size"],
  [1.5, "ping pong ball-size"],
  [1.25, "half dollar-size"],
  [1.0, "quarter-size"],
  [0.75, "penny-size"],
  [0.5, "marble-size"],
];

/**
 * What a person calls a stone that size.
 *
 * The Weather Service's own convention, and it is not folksiness for its own
 * sake: "1.75 inch hail" means nothing to a realtor and "golf ball-size hail"
 * means something to everybody. The number goes on the county report where it
 * can be checked; the sentence in the text says what it was like.
 */
export function hailSizeWords(inches: number | null): string | null {
  if (inches === null || !Number.isFinite(inches) || inches <= 0) return null;
  for (const [size, words] of HAIL_SIZES) {
    if (inches >= size) return words;
  }
  return "small";
}

/**
 * A weekday name, in Central time, from a `YYYY-MM-DD`.
 *
 * Parsed as noon UTC rather than midnight: midnight UTC on the 25th is 7pm
 * Central on the *24th*, and a text that says the storm was on Monday when the
 * county remembers Tuesday is the kind of small wrongness that makes the whole
 * message read as automated.
 */
export function weekdayName(eventDate: string): string {
  const at = new Date(`${eventDate}T12:00:00Z`);
  if (Number.isNaN(at.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    weekday: "long",
  }).format(at);
}

const listOf = (items: readonly string[]): string => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0]!;
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

const titleCase = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase())
    .replace(/\bMc([a-z])/g, (_m, next: string) => `Mc${next.toUpperCase()}`);

/**
 * A place name a person would recognise, out of what an LSR carries.
 *
 * The `CITY` field is not a city. It is the Weather Service's location string,
 * which is usually a town but is very often a bearing from a landmark —
 * "2 SE Waxahatchee At Lay", "1 NW Grayson Valley". Title-casing that verbatim
 * produces "2 Se Waxahatchee At Lay", which in the first line of a text message
 * reads as a machine emptying a database, which is exactly what it is.
 *
 * So the distance-and-bearing prefix comes off for the summary sentence. The
 * full string is kept untouched everywhere it is evidence — the county report's
 * table prints what the feed said.
 */
export function townName(city: string): string {
  const withoutBearing = city.trim().replace(/^\d+\s+[NSEW]{1,3}\s+/i, "");
  return titleCase(withoutBearing || city.trim());
}

/**
 * The one sentence that goes in every storm text.
 *
 * Written to sound like somebody who watched it happen, because it is the first
 * line of a message from a person. That rules out both directions people
 * usually go: no "SEVERE WEATHER ALERT", and no "significant convective
 * activity was observed". Facts, in the order somebody would say them, with the
 * towns named — a partner in Pelham reading "around Pelham and Alabaster" knows
 * instantly whether this is about them.
 *
 * At most three towns. The reports cluster, and a list of nine reads as a
 * machine emptying a database.
 */
export function stormSummary(assessment: StormAssessment): string {
  const county = WATCHED_COUNTIES.find(
    (row) => row.key === assessment.county,
  )!.label;
  const day = weekdayName(assessment.eventDate);
  const when = day ? ` ${day}` : "";

  const clauses: string[] = [];

  // Rain is only mentioned when it is the reason — when it cleared the
  // threshold. Six tenths of an inch is not news, and "0.6" of rain and wind
  // damage" as the opening line of a text makes everything after it less
  // believable. Found by running this over a real March afternoon.
  if (
    assessment.rainIn24h !== null &&
    assessment.triggers.includes("heavy_rain")
  ) {
    clauses.push(`${assessment.rainIn24h.toFixed(1)}" of rain`);
  }
  if (assessment.maxWindMph !== null && assessment.maxWindMph >= 40) {
    clauses.push(`${Math.round(assessment.maxWindMph)} mph gusts`);
  } else if (assessment.triggers.includes("wind")) {
    clauses.push("wind damage");
  }

  let sentence: string;
  if (clauses.length > 0) {
    sentence = `${county} got ${listOf(clauses)}${when}.`;
  } else if (assessment.triggers.includes("flood_warning")) {
    sentence = `${county} was under a flood warning${when}.`;
  } else {
    sentence = `${county} took a storm${when}.`;
  }

  if (assessment.triggers.includes("hail")) {
    const size = hailSizeWords(assessment.maxHailIn);
    const towns = assessment.hailTowns.slice(0, 3).map(townName);
    const where = towns.length > 0 ? ` around ${listOf(towns)}` : "";
    const what = size ? `${size} hail` : "hail";
    sentence =
      clauses.length > 0
        ? `${sentence.slice(0, -1)}, with ${what} reported${where}.`
        : `${county} got ${what}${when}${where}.`;
  }

  return sentence;
}

/**
 * The line above a batch in the review queue.
 *
 * Shorter than the summary and built for scanning a list: county, date, what
 * fired. The summary is the sentence a partner reads; this is the one Jonathon
 * reads at 8am to decide which batch to open first.
 */
export function stormBatchLabel(assessment: StormAssessment): string {
  const county = WATCHED_COUNTIES.find(
    (row) => row.key === assessment.county,
  )!.label;
  const names: Record<StormTrigger, string> = {
    hail: "hail",
    wind: "wind",
    heavy_rain: "heavy rain",
    flood_warning: "flooding",
  };
  const what = assessment.triggers.map((trigger) => names[trigger]);
  const date = new Date(`${assessment.eventDate}T12:00:00Z`);
  const when = Number.isNaN(date.getTime())
    ? assessment.eventDate
    : new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Chicago",
        month: "short",
        day: "numeric",
      }).format(date);
  return what.length > 0
    ? `${county}, ${when} — ${listOf(what)}`
    : `${county}, ${when}`;
}

/** Where the public county report for this storm lives. */
export function countyReportPath(
  county: WatchedCounty,
  eventDate: string,
): string {
  return `/storms/${county}/${eventDate}`;
}

/** `/storms/{county}/{date}`, and nothing that merely looks like it. */
export function countyReportFromPath(
  pathname: string,
): { county: WatchedCounty; date: string } | null {
  const match = /^\/storms\/(shelby|jefferson)\/(\d{4}-\d{2}-\d{2})\/?$/.exec(
    pathname,
  );
  if (!match) return null;
  return { county: match[1] as WatchedCounty, date: match[2]! };
}
